import { useCallback } from 'react';
import { UseQueryResult, UseQueryOptions, QueryKey, useQuery } from '@tanstack/react-query';
import { useAuth } from '@acast-tech/auth-client';
import { config } from '@/config';

type QueryType = string | string[] | number | undefined

/**
 * Stringify a query object
 * @param obj - The query object
 * @returns The stringified query
 */
const stringifyQuery = (obj: Record<string, QueryType>) => {
  const searchParams = new URLSearchParams();

  Object.entries(obj).forEach(([key, value]) => {
    if (value != null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v != null && v !== undefined && v !== '') {
            searchParams.append(key, v.toString());
          }
        });
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });

  return searchParams.toString();
};

/**
 * Build a clean URL with query parameters
 * @param path - The path to build the URL from
 * @param query - The query parameters
 * @returns The built URL
 */
export const buildUrl = (path: string, query: Record<string, QueryType>|undefined): string => {
  // Filter out undefined values
  const cleanQuery = Object.fromEntries(
    Object.entries(query ?? {}).filter(([, value]) => value !== undefined)
  );
  let url = path;
  if (Object.keys(cleanQuery).length > 0) {
    url += `?${stringifyQuery(cleanQuery)}`;
  }

  return url;
};

/**
 * API Error class
 * @param message - The error message
 * @param response - The response
 */
export class ApiError extends Error {
  response: Response;
  constructor(message: string, response: Response) {
    super(message);
    this.response = response;
  }
}

export const doFetch = async (path: string, token: string): Promise<Response> => {
  const baseUrl = config.revenueApiUrl;
  const url = `${baseUrl}${path}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new ApiError('Error fetching revenue API', response);
  }
  return response;
};

/**
 * Fetch data from revenue API
 * @param path - The path to fetch data from
 * @param token - The authentication token
 * @returns The fetched data
 */
export const get = async <T>(path: string, token: string): Promise<T> => {
  const response = await doFetch(path, token);
  return (await response.json()) as T;
};

export interface FileResponse {
  blob: Blob;
  fileName: string;
  contentType: string;
  warningMessage?: string;
}

export const getFile = async (path: string, token: string): Promise<FileResponse> => {
  const response = await doFetch(path, token);

  const fileName =
    response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'file';
  const contentType = response.headers.get('Content-Type') || 'application/pdf';

  return {
    blob: await response.blob(),
    fileName,
    contentType,
    warningMessage: response.headers.get('X-Warning-Message') ?? undefined,
  };
};

/**
 * Returns the API's validation message for a 400 response. The export handlers send these as
 * plain text; JSON error bodies from the error middleware are ignored so no internals leak.
 */
export const getApiValidationMessage = async (error: unknown): Promise<string | undefined> => {
  if (!(error instanceof ApiError) || error.response.status !== 400) {
    return undefined;
  }
  if (!error.response.headers.get('Content-Type')?.startsWith('text/')) {
    return undefined;
  }
  return (await error.response.text()) || undefined;
};

/**
 * Hook that returns a file fetcher with token and 401 logout/navigate handling (same pattern as useApi).
 * @returns Stable callback (path: string) => Promise<FileResponse>
 */
export const useFileFetcher = (): ((path: string) => Promise<FileResponse>) => {
  const { getToken, logout } = useAuth();

  return useCallback(
    async (path: string): Promise<FileResponse> => {
      try {
        const token = await getToken();
        return await getFile(path, token);
      } catch (err) {
        // TODO: move file fetching into a TanStack Query hook so QueryCache.onError
        // handles this logout side effect centrally
        if (err instanceof ApiError && err.response?.status === 401) {
          logout();
          throw err;
        }
        throw err;
      }
    },
    [getToken, logout]
  );
};

/**
 * Base hook for API calls to revenue API using react-query
 * @param key - The query key
 * @param fetcher - The fetcher function
 * @param options - The query options
 * @returns The query result
 */
export const useApi = <TData = unknown, TError = Error>(
  key: QueryKey,
  fetcher: (token: string) => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<TData, TError> => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: key,
    queryFn: async (): Promise<TData> => {
      const token = await getToken();
      return fetcher(token);
    },
    ...options,
  });
};

/**
 * Simplified API query hook. Uses the URL to build the cache key so we don't need to think about it.
 *
 * @param urlBuilder maps the params to the path and query needed by the endpoint. TODO: make it actually a URL.
 *   Omit (or pass `undefined`) when the query is not applicable — the hook stays mounted (rules of hooks)
 *   but never fetches, and does not call the builder for a query key.
 * @param params the parameters needed to build the URL
 * @returns
 */
export const useQueryUrl = <TData, TParams>(
  urlBuilder: ((params: TParams) => string) | undefined,
  params: TParams,
  options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>
) => {
  const url = urlBuilder?.(params);
  const fetcher = (token: string) => get<TData>(url!, token);
  const [path, query] = (url ?? '').split('?');
  const queryKey = url ? [...path.split('/'), query] : ['__disabled__'];

  return useApi(queryKey, fetcher, {
    ...options,
    enabled: Boolean(urlBuilder) && (options?.enabled ?? true),
  });
};
